import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiDelete, apiGet, apiPost, apiPut } from "../../shared/apiClient";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../../shared/ToastContext";

const inputClass =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20";

export function CustomFieldsPage() {
  const { user } = useAuth();
  const toast = useToast();
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [groups, setGroups] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [fieldForms, setFieldForms] = useState({});
  const [loading, setLoading] = useState(false);

  const effectiveCustomerId = useMemo(
    () => (isAdmin ? selectedCustomerId : user?.customerId || ""),
    [isAdmin, selectedCustomerId, user]
  );

  useEffect(() => {
    const loadCustomers = async () => {
      if (!isAdmin) return;
      const res = await apiGet("/api/Customers");
      if (res.ok) {
        const rows = Array.isArray(res.data) ? res.data : [];
        setCustomers(rows);
        if (!selectedCustomerId && rows[0]?.id) setSelectedCustomerId(String(rows[0].id));
      }
    };
    void loadCustomers();
  }, [isAdmin, selectedCustomerId]);

  const load = useCallback(async () => {
    if (!effectiveCustomerId) return;
    setLoading(true);
    const q = isAdmin ? `?customerId=${encodeURIComponent(effectiveCustomerId)}` : "";
    const res = await apiGet(`/api/CustomFieldGroups${q}`);
    setLoading(false);
    if (!res.ok) {
      toast.push(String(res.error || "Failed to load custom fields"), "error");
      return;
    }
    setGroups(Array.isArray(res.data) ? res.data : []);
  }, [effectiveCustomerId, isAdmin, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const createGroup = async (e) => {
    e.preventDefault();
    if (!groupName.trim() || !effectiveCustomerId) return;
    const res = await apiPost("/api/CustomFieldGroups", {
      name: groupName,
      customerId: Number(effectiveCustomerId)
    });
    if (!res.ok) {
      toast.push(String(res.error || "Failed to create group"), "error");
      return;
    }
    setGroupName("");
    toast.push("Group created", "success");
    await load();
  };

  const updateGroup = async (group, name) => {
    const res = await apiPut(`/api/CustomFieldGroups/${group.id}`, { ...group, name });
    if (!res.ok) toast.push(String(res.error || "Failed to update group"), "error");
    else await load();
  };

  const deleteGroup = async (group) => {
    const res = await apiDelete(`/api/CustomFieldGroups/${group.id}`);
    if (!res.ok) toast.push(String(res.error || "Failed to delete group"), "error");
    else {
      toast.push("Group deleted", "success");
      await load();
    }
  };

  const createField = async (groupId) => {
    const form = fieldForms[groupId] || { name: "", value: "" };
    if (!form.name?.trim()) return;
    const res = await apiPost(`/api/CustomFieldGroups/${groupId}/fields`, form);
    if (!res.ok) {
      toast.push(String(res.error || "Failed to create field"), "error");
      return;
    }
    setFieldForms((prev) => ({ ...prev, [groupId]: { name: "", value: "" } }));
    await load();
  };

  const updateField = async (field, patch) => {
    const res = await apiPut(`/api/CustomFieldGroups/fields/${field.id}`, { ...field, ...patch });
    if (!res.ok) toast.push(String(res.error || "Failed to update field"), "error");
    else await load();
  };

  const deleteField = async (field) => {
    const res = await apiDelete(`/api/CustomFieldGroups/fields/${field.id}`);
    if (!res.ok) toast.push(String(res.error || "Failed to delete field"), "error");
    else await load();
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Custom Fields</h1>
            <p className="mt-1 text-sm text-slate-500">Manage custom field groups and their values.</p>
          </div>
          {isAdmin && (
            <select className={inputClass} value={selectedCustomerId} onChange={(e) => setSelectedCustomerId(e.target.value)}>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.name || c.code || `Customer ${c.id}`}</option>
              ))}
            </select>
          )}
        </div>
        <form onSubmit={createGroup} className="mt-4 flex gap-2">
          <input className={inputClass} value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="New group name" />
          <button className="rounded-lg bg-brand-800 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700">Add Group</button>
        </form>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">Loading...</div>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => (
            <div key={group.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <input className={`${inputClass} max-w-md font-semibold`} defaultValue={group.name || ""} onBlur={(e) => e.target.value !== group.name && void updateGroup(group, e.target.value)} />
                <button type="button" onClick={() => void deleteGroup(group)} className="rounded-lg px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50">Delete Group</button>
              </div>
              <div className="mt-4 space-y-2">
                {(group.fields || []).map((field) => (
                  <div key={field.id} className="grid grid-cols-1 gap-2 rounded-xl bg-slate-50 p-3 md:grid-cols-[1fr_2fr_auto]">
                    <input className={inputClass} defaultValue={field.name || ""} onBlur={(e) => e.target.value !== field.name && void updateField(field, { name: e.target.value })} />
                    <input className={inputClass} defaultValue={field.value || ""} onBlur={(e) => e.target.value !== field.value && void updateField(field, { value: e.target.value })} />
                    <button type="button" onClick={() => void deleteField(field)} className="rounded-lg px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50">Delete</button>
                  </div>
                ))}
                <div className="grid grid-cols-1 gap-2 rounded-xl border border-dashed border-slate-300 p-3 md:grid-cols-[1fr_2fr_auto]">
                  <input className={inputClass} value={fieldForms[group.id]?.name || ""} onChange={(e) => setFieldForms((prev) => ({ ...prev, [group.id]: { ...(prev[group.id] || {}), name: e.target.value } }))} placeholder="Field name" />
                  <input className={inputClass} value={fieldForms[group.id]?.value || ""} onChange={(e) => setFieldForms((prev) => ({ ...prev, [group.id]: { ...(prev[group.id] || {}), value: e.target.value } }))} placeholder="Value" />
                  <button type="button" onClick={() => void createField(group.id)} className="rounded-lg bg-brand-800 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700">Add Field</button>
                </div>
              </div>
            </div>
          ))}
          {groups.length === 0 && <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">No custom field groups found.</div>}
        </div>
      )}
    </div>
  );
}
